import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { api, updatesApi, type TenantSummary } from '../api/client';
import { Icon, type IconName } from '../lib/icons';
import { accessTo, homeFor, useAuth } from '../lib/auth';
import { applyTheme, initials, loadTheme, type ThemeChoice } from '../lib/session';
import { useData } from '../lib/useData';
import { plural } from '../lib/format';
import { effectiveZone, setDisplayZone } from '../lib/time';
import { YourSettings } from './TimeZone';
import { LelandLauncher } from './Leland';
import { LelandProvider } from '../lib/leland';
import { RecordingChip, SessionRecording } from './SessionRecording';
import { useCompanyChanges, useUnread } from '../lib/messages';
import { useMessageAlerts } from '../lib/alerts';
import { teamSoundPrefs } from '../lib/teamSound';
import { setTeamSoundPrefs } from '../lib/teamSoundPlayer';
import { useCallLine } from '../lib/calls';
import { usePhoneCallsLive } from '../lib/phoneCalls';
import { useUnheardVoicemail, useVoicemailLive } from '../lib/voicemail';
import { useMissedLive } from '../lib/missed';
import { usePatchRingLive } from '../lib/patchRing';
import { useMeetingRingLive } from '../lib/meetingRing';
import { useHeadsetSurfaces } from '../lib/headset/surfaces';
import { usePicturesLive } from '../lib/pictures';
import { Face, YouPicture } from './PhotoEditor';
import { PHONE_APP_PAGES, billingShown, isMessagingOnly, userPages } from '../lib/portal';
import { MENU_FOLD, useFold } from '../lib/fold';
// The Stitch app on a phone, for the company's own people (Slack-style home, tabs at the bottom).
import { PhoneApp, PhoneShellContext, usePhone, type PhoneShell } from './PhoneApp';
import { LoadError, Loading, Sheet } from './ui';
import { PaymentBanner } from './PayInvoice';
// The installable app: install banner, tapped notifications, signing this device up again.
import { PwaPrompts } from './Notifications';
import { CallOfferPopups } from './CallOfferPopups';

export function ThemeToggle() {
  const [theme, setTheme] = useState<ThemeChoice>(loadTheme);
  useEffect(() => applyTheme(theme), [theme]);
  return (
    <div className="theme-toggle" role="group" aria-label="Theme">
      {(['light', 'dark', 'system'] as const).map((t) => (
        <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}>
          {t[0]!.toUpperCase() + t.slice(1)}
        </button>
      ))}
    </div>
  );
}

/** Which side a company admin who also has an extension is looking at: the company, or their own phone. */
type ViewMode = 'admin' | 'mine';
const viewKey = (domain: string) => `stitch.view.${domain.toLowerCase()}`;
function loadView(domain: string): ViewMode {
  try {
    return localStorage.getItem(viewKey(domain)) === 'mine' ? 'mine' : 'admin';
  } catch {
    return 'admin';
  }
}
function saveView(domain: string, mode: ViewMode): void {
  try {
    localStorage.setItem(viewKey(domain), mode);
  } catch {
    // Private mode: the switch still works for this visit.
  }
}

/** Admin | My phone, like the theme switch: for admins who also have an extension here. */
function ViewSwitch({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  return (
    <div className="theme-toggle view-switch" role="group" aria-label="View">
      <button type="button" aria-pressed={mode === 'admin'} onClick={() => onChange('admin')} title="Manage the company’s phone system">
        Admin
      </button>
      <button type="button" aria-pressed={mode === 'mine'} onClick={() => onChange('mine')}>
        My phone
      </button>
    </div>
  );
}

/** The version people see, from package.json at build time. */
declare const __STITCH_VERSION__: string;
const VERSION = typeof __STITCH_VERSION__ === 'string' ? __STITCH_VERSION__ : 'dev';

/** "Stitch 1.0.1", linking to what's new in it. */
export function VersionNote() {
  return (
    <Link className="version-note" to="/help/whats-new" title="What’s new in Stitch">
      Stitch {VERSION}
    </Link>
  );
}

/** Signing out, then the sign-in page. */
function useSignOut(): () => void {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return () => void signOut().then(() => navigate('/sign-in'));
}

/** Your settings, opened from the sidebar or (on a phone) from your account sheet. */
function useYourSettings(companyZone?: string, companyName?: string): { open: () => void; dialog: ReactNode } {
  const [shown, setShown] = useState(false);
  return {
    open: () => setShown(true),
    dialog: shown ? <YourSettings open onClose={() => setShown(false)} {...(companyZone ? { companyZone } : {})} {...(companyName ? { companyName } : {})} /> : null
  };
}

// --- The menu on a computer, and folding it to a rail of icons for more room ------------------

interface MenuFoldState {
  folded: boolean;
  /** Folds the menu, or opens it again (from one of its arrow buttons). */
  toggle: () => void;
  /** Set as an arrow button folds or opens the menu: the other arrow, where it now is, takes the focus. */
  moveFocus: { current: boolean };
}
const MenuFold = createContext<MenuFoldState>({ folded: false, toggle: () => undefined, moveFocus: { current: false } });

/** The arrow by the logo that folds the menu, and the one at the foot of the rail that opens it again. */
function MenuFoldButton() {
  const { folded, toggle, moveFocus } = useContext(MenuFold);
  const ref = useRef<HTMLButtonElement>(null);
  // The arrow that was pressed is gone: this one keeps the focus where it was.
  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    ref.current?.focus();
  }, [moveFocus]);
  const label = folded ? 'Open the menu' : 'Fold the menu';
  return (
    <button ref={ref} type="button" className="menu-fold" aria-expanded={!folded} aria-controls="app-menu" aria-label={label} {...(folded ? { 'data-tip': label } : { title: label })} onClick={toggle}>
      <Icon name={folded ? 'chevRight' : 'chevLeft'} size={16} strokeWidth={2.2} />
    </button>
  );
}

/**
 * The menu on a computer (a phone has its tab bar instead, and this stays hidden there). It folds
 * to a 64 px rail of icons, remembered on this device, and the page gets the room. Folded, an
 * icon's name shows beside it when it's pointed at or focused (fixed to the window, as the rail
 * clips), counts stay as badges and the section names become thin lines.
 */
function Sidebar({ label, children }: { label: string; children: ReactNode }) {
  const [folded, setFolded] = useFold(MENU_FOLD);
  const moveFocus = useRef(false);
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const toggle = () => {
    moveFocus.current = true;
    setTip(null);
    setFolded(!folded);
  };
  const show = (e: SyntheticEvent<HTMLElement>) => {
    if (!folded) return;
    const el = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-tip]') : null;
    if (!el || !e.currentTarget.contains(el) || !el.dataset.tip) return setTip(null);
    const r = el.getBoundingClientRect();
    setTip({ text: el.dataset.tip, x: Math.round(r.right + 10), y: Math.round(r.top + r.height / 2) });
  };
  const hide = () => setTip(null);
  return (
    <MenuFold.Provider value={{ folded, toggle, moveFocus }}>
      <nav
        id="app-menu"
        className={`sidebar${folded ? ' folded' : ''}`}
        aria-label={label}
        onMouseOver={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onScrollCapture={hide}
        onKeyDown={(e) => e.key === 'Escape' && hide()}
      >
        {children}
      </nav>
      {folded &&
        tip &&
        createPortal(
          <span className="rail-tip" aria-hidden="true" style={{ left: tip.x, top: tip.y }}>
            {tip.text}
          </span>,
          document.body
        )}
    </MenuFold.Provider>
  );
}

function SidebarFoot({ subtitle, onSettings }: { subtitle: string; onSettings: () => void }) {
  const { state } = useAuth();
  const signOut = useSignOut();
  const { folded } = useContext(MenuFold);
  const name = state?.user?.name || state?.user?.email || '';
  // Folded: the arrow that opens the menu again, and your picture (your settings).
  if (folded) {
    return (
      <div className="sidebar-foot">
        <MenuFoldButton />
        <button type="button" className="rail-you" aria-label="Your settings" data-tip={name ? `${name} · Your settings` : 'Your settings'} onClick={onSettings}>
          <Face userId={state?.user?.id} name={name} size="small" />
        </button>
      </div>
    );
  }
  return (
    <div className="sidebar-foot">
      <div className="who">
        <Face userId={state?.user?.id} name={name} size="small" />
        <span className="who-text">
          <strong>{name}</strong>
          <span>{subtitle}</span>
        </span>
        <button type="button" className="icon-btn" aria-label="Your settings" title="Your settings" onClick={onSettings}>
          <Icon name="sliders" size={16} />
        </button>
        <button type="button" className="icon-btn" aria-label="Sign out" title="Sign out" onClick={signOut}>
          <Icon name="logout" size={16} />
        </button>
      </div>
      <ThemeToggle />
      <VersionNote />
    </div>
  );
}

// --- On a phone: a slim bar at the top, the main pages in a tab bar at the bottom ----------------

/** A page in a phone's tab bar. */
interface Tab {
  to: string;
  icon: IconName;
  label: string;
  end?: boolean;
  count?: string;
}

/** A conversation open in Messages: on a phone it has the whole screen, like a messaging app. */
function useFocused(): boolean {
  const { pathname } = useLocation();
  return /\/messages\/[^/]+/.test(pathname);
}

/**
 * The menu as a phone shows it (the sidebar is for wider screens): at the top, Stitch and where you
 * are, with you (your settings, theme, switching company, Help, signing out); at the bottom, the
 * main pages, and More for all of them. Both step aside for an open conversation.
 */
function PhoneChrome(p: { home: string; place: string; subtitle: string; tabs: Tab[]; menu: ReactNode; switcher?: ReactNode; switchCompany?: boolean; onSettings: () => void }) {
  const { state } = useAuth();
  const signOut = useSignOut();
  const { pathname } = useLocation();
  const [sheet, setSheet] = useState<'menu' | 'you' | null>(null);
  // Following a link in a sheet closes it.
  useEffect(() => setSheet(null), [pathname]);
  const name = state?.user?.name || state?.user?.email || '';
  return (
    <>
      <header className="ptop">
        <Link to={p.home} className="ptop-brand" aria-label="Stitch home">
          <span className="brand-word">Stitch</span>
          <span className="brand-bar" />
        </Link>
        <span className="ptop-place">{p.place}</span>
        <button type="button" className="ptop-you" aria-label="You: your settings, theme and signing out" onClick={() => setSheet('you')}>
          <YouPicture name={name} />
        </button>
      </header>
      <nav className="ptabs" aria-label="Main pages">
        {p.tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className="ptab">
            <Icon name={t.icon} size={22} strokeWidth={1.8} />
            <span>{t.label}</span>
            {t.count ? <span className="ptab-count">{t.count}</span> : null}
          </NavLink>
        ))}
        <button type="button" className={`ptab${sheet === 'menu' ? ' active' : ''}`} aria-haspopup="dialog" onClick={() => setSheet('menu')}>
          <Icon name="menu" size={22} strokeWidth={1.8} />
          <span>More</span>
        </button>
      </nav>
      <Sheet open={sheet === 'menu'} onClose={() => setSheet(null)} title="All pages">
        <div className="nav psheet-nav">{p.menu}</div>
      </Sheet>
      <Sheet open={sheet === 'you'} onClose={() => setSheet(null)} title={name} subtitle={p.subtitle}>
        <div className="you-sheet">
          {p.switcher}
          <div className="you-rows">
            <button
              type="button"
              className="you-row"
              onClick={() => {
                setSheet(null);
                p.onSettings();
              }}
            >
              <Icon name="sliders" size={18} />
              Your settings
            </button>
            {p.switchCompany && (
              <Link to="/choose" className="you-row">
                <Icon name="building" size={18} />
                Switch company
              </Link>
            )}
            <Link to="/help" className="you-row">
              <Icon name="help" size={18} />
              Help center
            </Link>
            <button type="button" className="you-row" onClick={signOut}>
              <Icon name="logout" size={18} />
              Sign out
            </button>
          </div>
          <ThemeToggle />
          <VersionNote />
        </div>
      </Sheet>
    </>
  );
}

/**
 * The staff banner stays at the top while the page scrolls; the sidebar sits under it, filling
 * the rest of the window, so the menu scrolls and the person's details stay at the bottom.
 */
function useBannerHeight(): {
  ref: (el: HTMLDivElement | null) => void;
  style: CSSProperties;
} {
  const [h, setH] = useState(0);
  const watching = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLDivElement | null) => {
    watching.current?.disconnect();
    watching.current = null;
    if (!el) return setH(0);
    const measure = () => setH(Math.ceil(el.getBoundingClientRect().height));
    measure();
    watching.current = new ResizeObserver(measure);
    watching.current.observe(el);
  }, []);
  return { ref, style: { '--banner-h': `${h}px` } as CSSProperties };
}

/** Times on these pages: the person's choice, else the company's, else their device's. */
function useDisplayZone(companyZone: string | null | undefined): void {
  const { state } = useAuth();
  // Set before the pages below render, so every time they show is in this zone.
  setDisplayZone(effectiveZone(state?.user?.timeZone, companyZone));
}

/** A page's name for a screen reader while the menu is folded to icons: "Messages, 3", "Microsoft Teams, Soon". */
function foldedName(label: string, count?: ReactNode, tag?: string): string {
  return [label, typeof count === 'string' || typeof count === 'number' ? String(count) : '', tag ?? ''].filter(Boolean).join(', ');
}

function NavItem({ to, icon, label, end, count, countTone, tag }: { to: string; icon: IconName; label: string; end?: boolean; count?: ReactNode; countTone?: 'alert'; tag?: string }) {
  const { folded } = useContext(MenuFold);
  return (
    <NavLink to={to} end={end} data-tip={label} aria-label={folded ? foldedName(label, count, tag) : undefined}>
      <Icon name={icon} size={18} strokeWidth={1.75} />
      <span className="nav-text">{label}</span>
      {count !== undefined && count !== '' && <span className={`count${countTone ? ` ${countTone}` : ''}`}>{count}</span>}
      {tag && <span className="tag">{tag}</span>}
    </NavLink>
  );
}

/** What's unread in Messages (live), as shown on the menu: "", "3", "99+". Stitch staff aren't in customers' conversations. */
function useMessagesBadge(domain: string): string {
  const { state } = useAuth();
  const member = Boolean(state?.memberships?.some((m) => m.tenant === domain.toLowerCase()));
  const unread = useUnread(domain, state?.user?.id, member);
  return unread ? (unread > 99 ? '99+' : String(unread)) : '';
}

/** For the company's own people: the message sound, and notifications while Stitch isn't in front (lib/alerts). */
function useAlerts(domain: string): void {
  const { state } = useAuth();
  // Their own choices for a text to their ring group or queue (1.12.33), kept by Stitch.
  const team = state?.user?.teamTextSound;
  const me = state?.user?.id ?? '';
  useEffect(() => setTeamSoundPrefs(teamSoundPrefs(team), me), [team?.sound, team?.speak, team?.reminders, team?.volume, me]);
  useMessageAlerts(Boolean(state?.memberships?.some((m) => m.tenant === domain.toLowerCase())));
}

/** Messages in the menu, with what's unread. */
function MessagesItem({ domain, count }: { domain: string; count: string }) {
  return <NavItem to={`/t/${encodeURIComponent(domain)}/messages`} icon="chat" label="Messages" count={count} countTone={count ? 'alert' : undefined} />;
}

/**
 * Stitch Admin, for Stitch staff in the portal of a split Stitch (where they're members of their
 * own company, without the Admin menu): its own address, in its own tab.
 */
export function useStitchAdminUrl(): string | undefined {
  const { state } = useAuth();
  return state?.user?.staffAccount ? state.links?.admin : undefined;
}

function StitchAdminItem() {
  const url = useStitchAdminUrl();
  const { folded } = useContext(MenuFold);
  if (!url) return null;
  return (
    <a href={`${url}/admin`} target="_blank" rel="noopener" data-tip="Stitch Admin" aria-label={folded ? 'Stitch Admin' : undefined}>
      <Icon name="server" size={18} strokeWidth={1.75} />
      <span className="nav-text">Stitch Admin</span>
    </a>
  );
}

function Soon({ icon, label }: { icon: IconName; label: string }) {
  return (
    <span className="soon" aria-disabled="true" data-tip={`${label} (soon)`}>
      <Icon name={icon} size={18} strokeWidth={1.75} />
      <span className="nav-text">{label}</span>
      <span className="tag">Soon</span>
    </span>
  );
}

/** Stitch, by the arrow that folds the menu (folded: the S alone). */
function Brand({ to, pill, extra }: { to: string; pill?: string; extra?: ReactNode }) {
  const { folded } = useContext(MenuFold);
  return (
    <div className="brand-row">
      <Link to={to} className="brand" aria-label="Stitch home">
        <span className="brand-word">
          S<span className="brand-rest">titch</span>
        </span>
        <span className="brand-bar" />
      </Link>
      {pill && <span className="area-pill">{pill}</span>}
      {extra}
      {!folded && <MenuFoldButton />}
    </div>
  );
}

/** Sends people who aren't signed in (or whose session ended) to sign in. */
export function RequireSession({ children }: { children: ReactNode }) {
  const { state, error } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Stitch Admin's pages, where one address serves both: the Stitch team account's sign-in.
  const signInAt = /^\/(admin|live|setup)(\/|$)/.test(pathname) && state?.surface !== 'portal' ? '/sign-in?account=team' : '/sign-in';
  useEffect(() => {
    const onOut = () => navigate('/sign-in?expired=1');
    window.addEventListener('stitch:signed-out', onOut);
    return () => window.removeEventListener('stitch:signed-out', onOut);
  }, [navigate]);
  if (error && !state) return <LoadError error={new Error(error)} retry={() => window.location.reload()} />;
  if (!state) return <Loading label="Loading" />;
  if (!state.signedIn) return <Navigate to={signInAt} replace />;
  return <>{children}</>;
}

// --- Stitch Admin ----------------------------------------------------------------------------

export function AdminShell() {
  const { state } = useAuth();
  // Decide before loading anything: a customer never asks for staff-only data.
  if (!state?.user?.staff) return <Navigate to={state ? homeFor(state) : '/sign-in'} replace />;
  return <AdminLayout />;
}

function AdminLayout() {
  useDisplayZone(null);
  const health = useData(() => api.health(), []);
  const attention = useData(() => api.allChangeSets(['needs_attention'], 100), []);
  const tenants = useData(() => api.tenants(), []);

  const pbx = health.data?.pbx;
  const dot = !pbx ? '' : !pbx.reachable ? 'crit' : health.data?.writes?.readOnly ? 'warn' : '';
  const pbxTitle = health.data?.demo ? 'Demo PBX (sample data)' : 'Production PBX';
  const pbxText = !pbx ? 'Checking…' : pbx.reachable ? `Version ${pbx.version || 'unknown'}${health.data?.writes?.readOnly ? ' · read-only' : ''}` : 'Not reachable';
  const attentionCount = attention.data?.changeSets.length ?? 0;
  const support = useData(() => api.supportRequests('open'), []);
  // Companies waiting for an answer about more messaging-only people.
  const seatAsks = useData(() => api.seatRequests('waiting').catch(() => ({ requests: [], waiting: 0 })), []);
  // 1.13.0: features companies asked Stitch to turn on.
  const featureAsks = useData(() => api.featureRequests('open').catch(() => ({ requests: [], open: 0 })), []);
  // Customers that need an update (and were checked): the count beside Updates.
  const updatesList = useData(() => updatesApi.list().catch(() => ({ updates: [], mailboxPolicy: null })), []);
  const updatesDue = updatesList.data?.updates.reduce((n, u) => n + u.counts.needed + u.counts.partial, 0) ?? 0;
  const you = useYourSettings();
  const menu = (
    <>
      <NavItem to="/admin" end icon="grid" label="Overview" />
      <NavItem to="/admin/customers" icon="users" label="Customers" count={seatAsks.data?.waiting || ''} countTone={seatAsks.data?.waiting ? 'alert' : undefined} />
      <NavItem to="/admin/tenants" icon="building" label="Tenants" count={tenants.data?.tenants.length ?? ''} />
      <NavItem to="/admin/numbers" icon="hash" label="Numbers" />
      <NavItem to="/admin/billing" icon="receipt" label="Billing" />
      <NavItem to="/admin/texting" icon="send" label="Texting check" />
      {/* 1.12.33: every customer's updates (App calling, voicemail emails, old voicemail, Patch rings). */}
      <NavItem to="/admin/updates" icon="refresh" label="Updates" count={updatesDue || ''} />
      <NavItem to="/admin/activity" icon="list" label="Activity" count={attentionCount || ''} countTone={attentionCount ? 'alert' : undefined} />
      <NavItem to="/admin/staff" icon="key" label="Stitch team" />
      <NavItem to="/admin/system" icon="sliders" label="System" />
      {/* Storage A3 (3086): every company's archive, deletion runs, holds, archive-only companies. */}
      <NavItem to="/admin/archive" icon="archive" label="Archive" />
      <NavItem to="/admin/requests" icon="bell" label="Requests" count={featureAsks.data?.open || ''} countTone={featureAsks.data?.open ? 'alert' : undefined} />
      <NavItem to="/admin/support" icon="lifebuoy" label="Support" count={support.data?.open || ''} countTone={support.data?.open ? 'alert' : undefined} />
      <NavItem to="/admin/live" icon="screen" label="Live help" />
      <NavItem to="/admin/tests" icon="check" label="Test center" />
      {/* Real-world tests, their results, and one JSON export (Joey, Nov 1). */}
      <NavItem to="/admin/field-tests" icon="list" label="Field tests" />
      <NavItem to="/admin/changelog" icon="history" label="Changelog" />
      <span className="nav-label">Coming next</span>
      <Soon icon="chat" label="Microsoft Teams" />
      <NavItem to="/help" icon="help" label="Help center" />
    </>
  );
  return (
    <div className="app">
      <PhoneChrome
        home="/admin"
        place="Stitch Admin"
        subtitle="Stitch staff"
        tabs={[
          { to: '/admin', end: true, icon: 'grid', label: 'Overview' },
          { to: '/admin/customers', icon: 'users', label: 'Customers', count: seatAsks.data?.waiting ? String(seatAsks.data.waiting) : '' },
          {
            to: '/admin/support',
            icon: 'lifebuoy',
            label: 'Support',
            count: support.data?.open ? String(support.data.open) : ''
          },
          { to: '/admin/system', icon: 'sliders', label: 'System' }
        ]}
        menu={menu}
        onSettings={you.open}
      />
      <div className="app-body">
        <Sidebar label="Stitch Admin">
          <Brand to="/admin" pill="Admin" />
          <Link to="/admin/system" className="context-card" data-tip={`${pbxTitle} · ${pbxText}`}>
            <span className={`dot ${dot}`} aria-hidden="true" />
            <span className="context-text">
              <strong>{pbxTitle}</strong>
              <span>{pbxText}</span>
            </span>
          </Link>
          <div className="nav">{menu}</div>
          <SidebarFoot subtitle="Stitch staff" onSettings={you.open} />
        </Sidebar>
        <main className="main" id="main">
          <Outlet />
        </main>
      </div>
      {you.dialog}
      <PwaPrompts />
    </div>
  );
}

// --- Tenant portal ---------------------------------------------------------------------------

interface TenantCtx {
  tenant: TenantSummary;
  reload: () => void;
}
const TenantContext = createContext<TenantCtx | null>(null);

export function useTenant(): TenantCtx {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant outside TenantShell');
  return ctx;
}

export function TenantShell() {
  const { tenant: domain = '' } = useParams();
  const { state } = useAuth();
  const access = accessTo(state, domain);
  // Only ever a company the person belongs to; nothing is even asked about others.
  if (!access) return <Navigate to={state ? homeFor(state) : '/sign-in'} replace />;
  // Archive only (1.13.0, 2717): Call history, Storage, legal holds, Activity and Billing, nothing live.
  if (state?.memberships?.find((m) => m.tenant === domain.toLowerCase())?.archiveOnly) return <ArchiveOnlyLayout domain={domain} admin={access === 'admin'} />;
  // End users have their own phone, not the company's admin.
  if (access === 'user') return <UserLayout domain={domain} />;
  return <AdminOrMine domain={domain} />;
}

/** A company admin with their own extension can switch between the company and their own phone. */
function AdminOrMine({ domain }: { domain: string }) {
  const { state } = useAuth();
  const navigate = useNavigate();
  const m = state?.memberships?.find((x) => x.tenant === domain.toLowerCase());
  const canSwitch = Boolean(m?.role === 'admin' && m.extension && !state?.user?.staff);
  const [mode, setMode] = useState<ViewMode>(() => loadView(domain));
  const base = `/t/${encodeURIComponent(domain)}`;
  const change = (next: ViewMode) => {
    setMode(next);
    saveView(domain, next);
    navigate(next === 'mine' ? `${base}/me` : base);
  };
  const switcher = canSwitch ? <ViewSwitch mode={mode} onChange={change} /> : undefined;
  // On a phone there's one app: their own phone and calls, with the company's admin under More.
  const phone = usePhone();
  if (canSwitch && mode === 'mine' && !phone) return <UserLayout domain={domain} switcher={switcher} />;
  return <TenantLayout domain={domain} switcher={switcher} />;
}

function TenantLayout({ domain, switcher }: { domain: string; switcher?: ReactNode }) {
  const { state } = useAuth();
  const t = useData(() => api.tenant(domain), [domain]);
  const health = useData(() => api.health(), []);
  const staff = Boolean(state?.user?.staff);
  const many = (state?.memberships ?? []).filter((m) => m.tenant && m.role === 'admin').length > 1;
  const tenant = t.data?.tenant;
  const base = `/t/${encodeURIComponent(domain)}`;
  const companyZone = tenant?.timeZone ?? state?.memberships?.find((x) => x.tenant === domain.toLowerCase())?.timeZone ?? state?.timeZone;
  useDisplayZone(companyZone);
  const banner = useBannerHeight();
  const unread = useMessagesBadge(domain);
  useAlerts(domain);
  useCallLine(domain);
  usePhoneCallsLive(domain, state?.memberships?.find((x) => x.tenant === domain.toLowerCase())?.extension ?? undefined, Boolean(state?.signedIn));
  useVoicemailLive(domain, Boolean(state?.memberships?.find((x) => x.tenant === domain.toLowerCase())?.extension));
  useMissedLive(domain, Boolean(state?.memberships?.some((x) => x.tenant === domain.toLowerCase())));
  usePatchRingLive(domain, state?.user?.id, Boolean(state?.memberships?.some((x) => x.tenant === domain.toLowerCase())));
  useMeetingRingLive(domain, state?.user?.id, Boolean(state?.memberships?.some((x) => x.tenant === domain.toLowerCase())));
  // A headset's call and mute buttons (3141): for people in the company, not staff looking in.
  useHeadsetSurfaces(Boolean(state?.memberships?.some((x) => x.tenant === domain.toLowerCase())));
  // Staff viewing a company aren't in it: no live stream for them (it would be refused).
  usePicturesLive(domain, Boolean(state?.signedIn), Boolean(state?.memberships?.some((x) => x.tenant)));
  useCompanyChanges(domain, Boolean(state?.memberships?.some((x) => x.tenant === domain.toLowerCase())));
  const you = useYourSettings(companyZone, tenant?.name);
  const focused = useFocused();
  const phone = usePhone();
  const signOut = useSignOut();
  const { pathname } = useLocation();
  // The company's own people get the phone app on a phone; Stitch staff keep the portal's menu.
  const member = !staff;
  const membership = state?.memberships?.find((x) => x.tenant === domain.toLowerCase());
  // Billing (1.13.0): for staff, and for the company once staff turn "Customers see Billing" on.
  const billing = billingShown(staff, membership);
  // In the phone app, the company's admin pages are under More (its Home is Messages; the admin home is Overview).
  const menuFor = (inApp: boolean) => (
    <>
      <NavItem to={inApp ? `${base}/overview` : base} end icon="home" label={inApp ? 'Overview' : 'Home'} />
      {tenant?.customer?.status === 'onboarding' && <NavItem to={`${base}/setup`} icon="flag" label="Get set up" />}
      {!inApp && <MessagesItem domain={domain} count={unread} />}
      {!inApp && <NavItem to={`${base}/meetings`} icon="video" label="Meetings" />}
      {membership?.seat === 'receptionist' && membership.extension && <NavItem to={`${base}/console`} icon="headset" label="Console" />}
      <NavItem to={`${base}/people`} icon="users" label="People" />
      <NavItem to={`${base}/voicemail`} icon="mail" label="Voicemail boxes" />
      <NavItem to={`${base}/calls`} icon="phone" label="Call history" />
      <NavItem to={`${base}/history`} icon="history" label="Change history" />
      <span className="nav-label">Phones</span>
      <NavItem to={`${base}/phones`} icon="device" label="Desk phones" />
      <NavItem to={`${base}/templates`} icon="grid" label="Phone templates" />
      <NavItem to={`${base}/numbers`} icon="hash" label="Numbers" />
      <NavItem to={`${base}/emergency`} icon="pin" label="Emergency addresses" />
      <span className="nav-label">Call routing</span>
      <NavItem to={`${base}/auto-attendants`} icon="flow" label="Auto Attendants" />
      <NavItem to={`${base}/ring-groups`} icon="bell" label="Ring Groups" />
      <NavItem to={`${base}/time-rules`} icon="clock" label="Time of Day Rules" />
      <NavItem to={`${base}/call-park`} icon="park" label="Call Park" />
      <span className="nav-label">Contact Center</span>
      <NavItem to={`${base}/queues`} icon="headset" label="Call Queues" />
      <NavItem to={`${base}/wallboard`} icon="chart" label="Wallboard" />
      <NavItem to={`${base}/reports`} icon="report" label="Reports" />
      <span className="nav-label">Integrations</span>
      <NavItem to={`${base}/teams`} icon="chat" label="Microsoft Teams" tag="Soon" />
      <span className="nav-label">Company</span>
      <NavItem to={`${base}/team`} icon="key" label="Portal access" />
      {billing && <NavItem to={`${base}/billing`} icon="receipt" label="Billing" />}
      <NavItem to={`${base}/settings`} icon="sliders" label="Settings" />
      <NavItem to={`${base}/activity`} icon="list" label="Activity" />
      <span className="nav-label">Assistant</span>
      <NavItem to={`${base}/leland`} icon="spark" label="Leland" />
      {!inApp && <NavItem to="/help" icon="help" label="Help center" />}
      {!inApp && <StitchAdminItem />}
    </>
  );
  const menu = menuFor(false);
  const shell: PhoneShell = {
    base,
    company: tenant?.name ?? membership?.customerName ?? domain,
    subtitle: membership?.extension ? `Admin · Ext ${membership.extension}` : 'Admin',
    membership,
    admin: true,
    adminMenu: menuFor(true),
    switchCompany: many,
    openSettings: you.open,
    signOut
  };
  // A phone opens on the app's Home (Messages), not the admin home.
  if (member && phone && (pathname === base || pathname === `${base}/`)) return <Navigate to={`${base}/messages`} replace />;
  // Stitch staff looking at an archive-only company (2717) see what its people see.
  if (tenant?.customer?.status === 'archive_only') return <ArchiveOnlyLayout domain={domain} admin />;

  return (
    <LelandProvider domain={domain}>
      <div className={`app${member ? ' phone-app' : ''}${focused ? ' focused' : ''}`} style={banner.style}>
        {staff && (
          <div className="banner staff-banner" role="status" ref={banner.ref}>
            <Icon name="eye" size={18} strokeWidth={2} />
            <strong>Viewing {tenant?.name ?? domain} as the customer</strong>
            <span className="grow">{state?.user?.name || 'Stitch staff'} · Stitch staff · Changes you make are recorded under your name</span>
            <Link to="/admin/tenants">Exit to Stitch Admin</Link>
          </div>
        )}
        {member ? (
          <PhoneApp
            base={base}
            domain={domain}
            company={shell.company}
            myId={state?.user?.id}
            myName={state?.user?.name || state?.user?.email || ''}
            phone={Boolean(membership?.extension)}
            switchCompany={many}
          />
        ) : (
          <PhoneChrome
            home={base}
            place={tenant?.name ?? domain}
            subtitle="Stitch staff"
            tabs={[
              { to: base, end: true, icon: 'home', label: 'Home' },
              { to: `${base}/people`, icon: 'users', label: 'People' },
              { to: `${base}/calls`, icon: 'phone', label: 'Calls' }
            ]}
            menu={menu}
            switcher={switcher}
            switchCompany={many}
            onSettings={you.open}
          />
        )}
        <div className="app-body">
          <Sidebar label={`${tenant?.name ?? domain} admin`}>
            <Brand to={base} extra={!staff && !state?.user?.staffAccount ? <RecordingChip place="menu" /> : undefined} />
            <div className="context-card" data-tip={tenant?.name ?? domain}>
              <span className="avatar" aria-hidden="true">
                {initials(tenant?.name ?? domain)}
              </span>
              <span className="context-text">
                <strong>{tenant?.name ?? domain}</strong>
                <span>
                  {tenant?.extensionCount !== undefined ? plural(tenant.extensionCount, 'person', 'people') : domain}
                  {tenant && !tenant.writable ? ' · read-only' : ''}
                  {health.data?.demo ? ' · demo data' : ''}
                </span>
              </span>
            </div>
            {switcher}
            {many && (
              <Link to="/choose" className="link-btn switch-company">
                Switch company
              </Link>
            )}
            <div className="nav">{menu}</div>
            <SidebarFoot subtitle={staff ? 'Stitch staff' : 'Admin'} onSettings={you.open} />
          </Sidebar>
          <main className="main" id="main">
            {t.error ? (
              <div className="content">
                <div className="card">
                  <LoadError error={t.error} retry={t.reload} />
                </div>
              </div>
            ) : !tenant ? (
              <Loading label="Loading account" />
            ) : (
              <TenantContext.Provider value={{ tenant, reload: t.reload }}>
                {/* An invoice whose payment failed or is past due (3091), over every admin page but Billing's own; only where Billing shows. */}
                {billing && !/\/billing(\/|$)/.test(pathname) && <PaymentBanner domain={domain} />}
                <PhoneShellContext.Provider value={member ? shell : null}>
                  <Outlet />
                </PhoneShellContext.Provider>
              </TenantContext.Provider>
            )}
          </main>
        </div>
        <LelandLauncher base={base} />
        {/* Stitch staff are never recorded, also as members of their own company (the portal of a split Stitch). */}
        {!staff && !state?.user?.staffAccount && <SessionRecording domain={domain} />}
        {/* Call for you (Ask by message, 1.13.0), on whatever page they're on. */}
        {membership && <CallOfferPopups domain={domain} />}
        {you.dialog}
        <PwaPrompts banner={!(member && phone)} />
      </div>
    </LelandProvider>
  );
}

// --- End users: their own phone -------------------------------------------------------------

function UserLayout({ domain, switcher }: { domain: string; switcher?: ReactNode }) {
  const { state } = useAuth();
  const m = state?.memberships?.find((x) => x.tenant === domain.toLowerCase());
  const t = useData(() => api.tenant(domain), [domain]);
  const tenant = t.data?.tenant;
  const base = `/t/${encodeURIComponent(domain)}`;
  const many = (state?.memberships ?? []).filter((x) => x.tenant).length > 1;
  const companyZone = tenant?.timeZone ?? m?.timeZone ?? state?.timeZone;
  useDisplayZone(companyZone);
  const { pathname } = useLocation();
  // What their company's own role adds (call history, wallboard, reports), and their own phone if they have one.
  const perms = m?.permissions ?? {};
  // Messaging-only people (no extension): Messages, their own settings and Help (lib/portal.ts).
  const pages: string[] = userPages(m);
  const messagingOnly = isMessagingOnly(m);
  // They need nothing from the phone system: if it can't be reached just now, Messages still opens.
  const shown: TenantSummary | undefined =
    tenant ??
    (t.error && messagingOnly && m
      ? {
          domain: m.tenant,
          name: m.customerName,
          writable: false,
          ...(m.timeZone ? { timeZone: m.timeZone } : {})
        }
      : undefined);
  const home = `${base}/${pages[0] ?? 'me'}`;
  const here = pathname.slice(base.length).replace(/^\//, '').split('/')[0] ?? '';
  const hereRaw = pathname.startsWith(`/t/${domain}/`) ? (pathname.slice(`/t/${domain}/`.length).split('/')[0] ?? '') : here;
  const unread = useMessagesBadge(domain);
  useAlerts(domain);
  useCallLine(domain);
  usePhoneCallsLive(domain, m?.extension ?? undefined, Boolean(state?.signedIn));
  useVoicemailLive(domain, Boolean(m?.extension));
  useMissedLive(domain, Boolean(m));
  usePatchRingLive(domain, state?.user?.id, Boolean(m));
  useMeetingRingLive(domain, state?.user?.id, Boolean(m));
  useHeadsetSurfaces(Boolean(m));
  usePicturesLive(domain, Boolean(state?.signedIn), Boolean(state?.memberships?.some((x) => x.tenant)));
  useCompanyChanges(domain, Boolean(m));
  const unheard = useUnheardVoicemail(domain);
  const you = useYourSettings(companyZone, m?.customerName);
  const focused = useFocused();
  const phone = usePhone();
  const signOut = useSignOut();
  const appPage = (x: string) => (PHONE_APP_PAGES as readonly string[]).includes(x);
  // The company's admin pages aren't for end users: their own phone, and what their role allows, is
  // (and the phone app's own tabs).
  if (!pages.includes(here) && !pages.includes(hereRaw) && !appPage(here) && !appPage(hereRaw)) return <Navigate to={phone ? `${base}/messages` : home} replace />;
  const subtitle = m?.extension ? `Ext ${m.extension}` : messagingOnly ? 'Messaging only' : '';
  const menu = (
    <>
      {m?.extension && (
        <>
          <NavItem to={`${base}/me`} end icon="device" label="My phone" count={unheard ? (unheard > 99 ? '99+' : String(unheard)) : ''} />
          <NavItem to={`${base}/me/calls`} icon="phone" label="My calls" />
        </>
      )}
      {pages.includes('console') && <NavItem to={`${base}/console`} icon="headset" label="Console" />}
      {pages.includes('messages') && <MessagesItem domain={domain} count={unread} />}
      {pages.includes('meetings') && <NavItem to={`${base}/meetings`} icon="video" label="Meetings" />}
      {m?.roleName && <span className="nav-label">{m.roleName}</span>}
      {perms.calls && <NavItem to={`${base}/calls`} icon="history" label="Call history" />}
      {(perms.wallboard || perms.agents) && <NavItem to={`${base}/wallboard`} icon="chart" label="Wallboard" />}
      {perms.reports && <NavItem to={`${base}/reports`} icon="report" label="Reports" />}
      {perms.billing && billingShown(state?.user?.staff, m) && <NavItem to={`${base}/billing`} icon="receipt" label="Billing" />}
      <NavItem to="/help" icon="help" label="Help" />
      <StitchAdminItem />
    </>
  );
  const shell: PhoneShell = {
    base,
    company: m?.customerName ?? tenant?.name ?? domain,
    subtitle,
    membership: m,
    admin: false,
    adminMenu: null,
    switchCompany: many,
    openSettings: you.open,
    signOut
  };
  return (
    <div className={`app phone-app${focused ? ' focused' : ''}`}>
      <PhoneApp base={base} domain={domain} company={shell.company} myId={state?.user?.id} myName={state?.user?.name || state?.user?.email || ''} phone={Boolean(m?.extension)} switchCompany={many} />
      <div className="app-body">
        <Sidebar label={m?.extension ? 'Your phone' : 'Your account'}>
          <Brand to={home} extra={<RecordingChip place="menu" />} />
          <div className="context-card" data-tip={m?.customerName ?? tenant?.name ?? domain}>
            <span className="avatar" aria-hidden="true">
              {initials(m?.customerName ?? domain)}
            </span>
            <span className="context-text">
              <strong>{m?.customerName ?? tenant?.name ?? domain}</strong>
              <span>{m?.extension ? `Your extension ${m.extension}` : messagingOnly ? 'Messaging only' : 'Your account'}</span>
            </span>
          </div>
          {switcher}
          {many && (
            <Link to="/choose" className="link-btn switch-company">
              Switch company
            </Link>
          )}
          <div className="nav">{menu}</div>
          <SidebarFoot subtitle={subtitle} onSettings={you.open} />
        </Sidebar>
        <main className="main" id="main">
          {t.error && !shown ? (
            <div className="content">
              <div className="card">
                <LoadError error={t.error} retry={t.reload} />
              </div>
            </div>
          ) : !shown ? (
            <Loading label="Loading" />
          ) : (
            <TenantContext.Provider value={{ tenant: shown, reload: t.reload }}>
              <PhoneShellContext.Provider value={shell}>
                <Outlet />
              </PhoneShellContext.Provider>
            </TenantContext.Provider>
          )}
        </main>
      </div>
      {!state?.user?.staff && !state?.user?.staffAccount && <SessionRecording domain={domain} />}
      {m && <CallOfferPopups domain={domain} />}
      {you.dialog}
      <PwaPrompts banner={!phone} />
    </div>
  );
}

// --- Archive only (1.13.0, Teampace 2717) ----------------------------------------------------

/** The pages an archive-only company still has: the first part of the address after /t/<tenant>/. */
const ARCHIVE_ONLY_PAGES = ['calls', 'settings/storage', 'legal-holds', 'activity', 'billing'] as const;

/**
 * A company whose service with Stitch ended: Call history (from the archive), Storage and retention,
 * legal holds, Activity and Billing for its admins; Call history (their own calls, or what their role
 * shows) and Billing (if their role has it) for everyone else; and Help. Nothing live: no phone line,
 * Messages, alerts, Leland or session recording, and the phone system is never asked anything.
 */
function ArchiveOnlyLayout({ domain, admin }: { domain: string; admin: boolean }) {
  const { state } = useAuth();
  const t = useData(() => api.tenant(domain), [domain]);
  const tenant = t.data?.tenant;
  const m = state?.memberships?.find((x) => x.tenant === domain.toLowerCase());
  const staff = Boolean(state?.user?.staff);
  const base = `/t/${encodeURIComponent(domain)}`;
  const many = (state?.memberships ?? []).filter((x) => x.tenant).length > 1;
  const companyZone = tenant?.timeZone ?? m?.timeZone ?? state?.timeZone;
  useDisplayZone(companyZone);
  const banner = useBannerHeight();
  const company = tenant?.name ?? m?.customerName ?? domain;
  const you = useYourSettings(companyZone, company);
  const { pathname } = useLocation();
  // Billing (1.13.0): only once staff turn "Customers see Billing" on (staff always see it).
  const billing = (admin || Boolean(m?.permissions?.billing)) && billingShown(staff, m);
  const pages = ARCHIVE_ONLY_PAGES.filter((p) => admin || p === 'calls' || (p === 'billing' && billing));
  const here = decodeURIComponent(pathname).slice(`/t/${domain}`.length).replace(/^\//, '');
  if (!pages.some((p) => here === p || here.startsWith(`${p}/`))) return <Navigate to={`${base}/calls`} replace />;
  const since = tenant?.archiveOnly?.since ? new Date(tenant.archiveOnly.since).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  // "Call history, Storage and retention, legal holds, Activity and Billing stay", or "Call history stays".
  const keptList = ['Call history', ...(admin ? ['Storage and retention', 'legal holds', 'Activity'] : []), ...(billing ? ['Billing'] : [])];
  const kept = keptList.length === 1 ? `${keptList[0]} stays` : `${keptList.slice(0, -1).join(', ')} and ${keptList[keptList.length - 1]} stay`;
  const menu = (
    <>
      <span className="nav-label">Archive</span>
      <NavItem to={`${base}/calls`} icon="history" label="Call history" />
      {admin && <NavItem to={`${base}/settings/storage`} icon="server" label="Storage and retention" />}
      {admin && <NavItem to={`${base}/legal-holds`} icon="lock" label="Legal holds" />}
      {admin && <NavItem to={`${base}/activity`} icon="list" label="Activity" />}
      {billing && <NavItem to={`${base}/billing`} icon="receipt" label="Billing" />}
      <NavItem to="/help/archive-only" icon="help" label="Help center" />
      <StitchAdminItem />
    </>
  );
  const subtitle = staff ? 'Stitch staff' : admin ? 'Admin · archive only' : 'Archive only';
  return (
    <div className="app" style={banner.style}>
      <div className="banner staff-banner archive-banner" role="status" ref={banner.ref}>
        <Icon name={staff ? 'eye' : 'history'} size={18} strokeWidth={2} />
        <strong>{staff ? `Viewing ${company}: archive only` : `${company} is archive only`}</strong>
        <span className="grow">
          {`Its service with Stitch has ended${since ? ` (since ${since})` : ''}. ${kept} here; everything else is off.`}
        </span>
        {staff ? <Link to="/admin/customers">Exit to Stitch Admin</Link> : <Link to="/help/archive-only">What this means</Link>}
      </div>
      <PhoneChrome
        home={`${base}/calls`}
        place={company}
        subtitle={subtitle}
        tabs={[
          { to: `${base}/calls`, icon: 'history', label: 'History' },
          ...(admin ? [{ to: `${base}/settings/storage`, icon: 'server' as IconName, label: 'Storage' }] : []),
          ...(billing ? [{ to: `${base}/billing`, icon: 'receipt' as IconName, label: 'Billing' }] : [])
        ]}
        menu={menu}
        switchCompany={many}
        onSettings={you.open}
      />
      <div className="app-body">
        <Sidebar label={`${company}: archive`}>
          <Brand to={`${base}/calls`} pill="Archive" />
          <div className="context-card" data-tip={company}>
            <span className="avatar" aria-hidden="true">
              {initials(company)}
            </span>
            <span className="context-text">
              <strong>{company}</strong>
              <span>{since ? `Archive only since ${since}` : 'Archive only'}</span>
            </span>
          </div>
          {many && (
            <Link to="/choose" className="link-btn switch-company">
              Switch company
            </Link>
          )}
          <div className="nav">{menu}</div>
          <SidebarFoot subtitle={subtitle} onSettings={you.open} />
        </Sidebar>
        <main className="main" id="main">
          {t.error ? (
            <div className="content">
              <div className="card">
                <LoadError error={t.error} retry={t.reload} />
              </div>
            </div>
          ) : !tenant ? (
            <Loading label="Loading the archive" />
          ) : (
            <TenantContext.Provider value={{ tenant, reload: t.reload }}>
              <PhoneShellContext.Provider value={null}>
                <Outlet />
              </PhoneShellContext.Provider>
            </TenantContext.Provider>
          )}
        </main>
      </div>
      {you.dialog}
    </div>
  );
}
